<template>
  <Toast ref="toast" />

  <div class="page-title-container" style="padding-top: 0rem;">
    <h1 class="title-text">{{ steps[step].title }}</h1>
    <h2 class="subtitle-text">{{ steps[step].subtitle }}</h2>
  </div>

  <div class="center-items">
    <div class="steps-container">
      <button
        @click="step = 0"
        :class="{
          'primary-button medium-button': step === 0,
          'secondary-button medium-button': step !== 0,
        }"
        style="margin-right: 2rem;"
        type="button"
      >
        1 - SELECT
      </button>
      <i class="fas fa-chevron-right"></i>
      <button
        @click="step = 1"
        :class="{
          'primary-button medium-button': step === 1,
          'secondary-button medium-button': step !== 1,
        }"
        style="margin-right: 2rem; margin-left: 2rem;"
        type="button"
      >
        2 - JOB OFFER
      </button>
      <i class="fas fa-chevron-right"></i>
      <button
        @click="step = 2"
        :class="{
          'primary-button medium-button': step === 2,
          'secondary-button medium-button': step !== 2,
        }"
        style="margin-left: 2rem;"
        type="button"
      >
        3 - COMPANY
      </button>
    </div>
  </div>

  <div v-if="step === 0" id="step1">
    <div class="page-container">
      <p v-if="errors.showSelectSitesMissing" class="p-error">
        ⚠️ showSelectSitesMissing
      </p>

      <SitesGrid
        :initial-selected-sites="utils.localStorageParse('selectedSites', 'Array')"
        ref="sitesGrid"
        @selected-sites="
          selectedSites = $event;
          setSelectedSitesLocalStorage();
        "
        :has-filter="true"
        show-add-button
      />

      <div class="step1-sites-selected-container">
        <p v-if="subscription.purchasedJobPosts !== 0" class="form-title-text" style="margin-top: 0rem;">
          🚀 SITES SELECTED:
          <span style="color: var(--primary-color);"> {{ selectedSites.length }} / {{ subscription.purchasedJobPosts }} </span>
        </p>
        <p v-if="subscription.purchasedJobPosts === 0" class="form-title-text" style="margin-top: 0rem;">
          🚀 SITES SELECTED:
          <span style="color: var(--primary-color);">{{ selectedSites.length }}</span>
        </p>

        <div class="step1-sites-selected">
          <div :key="`sites-preview-${i}`" class="step1-sites-preview" v-for="(s, i) in selectedSites">
            <div class="website-name-container">
              <div class="dashboard-image-icon-container">
                <img class="image-hero" :src="s.logoUrl" />
              </div>
              &nbsp;
              <p class="dashboard-details-site-name">{{ s.name }}</p>
            </div>
            <button @click="$refs.sitesGrid.removeSite(s)" class="tertiary-button small-button step1-selector-add-button" type="button">
              Remove <i class="fas fa-times-circle delete-button"></i>
            </button>
          </div>

          <div
            v-if="subscription.activeUserPlans.length === 0"
            @click="$router.push({ path: '/suggest-site' })"
            class="step1-site-selector-unlock-more"
          >
            <div class="website-name-container">
              <p class="dashboard-details-site-name">
                🔎 Can't find your favourite site?
              </p>
            </div>
            <button
              @click="$router.push({ path: '/suggest-site' })"
              class="tertiary-button small-button step1-selector-add-button"
              type="button"
            >
              Suggest a new site
            </button>
          </div>
        </div>
      </div>

      <div class="center-items">
        <button @click="step++" class="primary-button" style="padding: 1rem 3rem;" type="button">
          Next step <i class="fas fa-arrow-right next-page-button-icon"></i>
        </button>
      </div>
    </div>
  </div>

  <div v-if="step === 1" id="step2">
    <div class="page-container">
      <div class="features-container">
        <!-- Job title -->
        <div class="form-container">
          <p class="form-title-text">JOB TITLE*</p>
          <input
            class="input-container input-container-medium"
            style="width: 100%;"
            placeholder="Example: Senior Software Engineer"
            ref="jobTitle"
            v-model="jobTitle"
            @click="select($refs.jobTitle, jobTitle)"
            type="text"
            @keyup.enter="select($refs.location, location)"
            maxlength="1000"
          />
          <p class="form-info-text">
            The job title is a key part of the job offer, be precise. A broader title will lower the quality of applicants.
          </p>

          <p v-if="errors.showJobTitleMissing" class="p-error">
            ⚠️ showJobTitleMissing
          </p>
        </div>

        <!-- Location -->
        <div class="form-container">
          <p class="form-title-text">LOCATION*</p>

          <input @click="showLocations = false" v-model="isFullRemote" type="checkbox" style="margin-bottom: 1.5rem; margin-top: 1rem;" />
          Worldwide remote &nbsp;
          <span class="featured-text">Access to a worldwide pool of talent</span>
          <div v-if="!isFullRemote">
            <input v-model="location" list="country-list" class="input-container input-container-medium" />
            <datalist id="country-list">
              <option v-for="(l, index) in countriesNames" :key="`countriesNames-${index}`" :value="l">{{ l }}</option>
            </datalist>
            <button type="button" class="primary-button medium-button" style="margin-top: 5px;" @click="location = ''">
              Clear
            </button>
          </div>

          <p class="form-info-text">
            A "Worldwide remote" position is highly recommended as you'll have access to a worldwide pool of talent.
          </p>
          <p v-if="errors.showLocationMissing" class="p-error">
            ⚠️ showLocationMissing
          </p>
        </div>

        <!-- Job description -->
        <div class="form-container">
          <div style="margin-bottom: 1.5rem;">
            <p class="form-title-text">JOB DESCRIPTION*</p>

            <input
              @change="useComplexDescription = !useComplexDescription"
              type="checkbox"
              :checked="!useComplexDescription"
              style="margin-bottom: 1.5rem; margin-top: 1rem;"
            />
            Simple
            <input
              @change="useComplexDescription = !useComplexDescription"
              type="checkbox"
              :checked="useComplexDescription"
              style="margin-bottom: 1.5rem; margin-top: 0.5rem;"
            />
            Detailed &nbsp;
            <span class="featured-text">Highly recommended for increasing candidates quality</span>

            <div v-if="!useComplexDescription">
              <p class="form-title-text">Write your custom job description</p>
              <textarea
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
                v-model="jobDescription"
              ></textarea>
            </div>

            <div v-if="useComplexDescription">
              <p class="form-title-text">Introduction</p>
              <textarea
                v-model="introduction"
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
              ></textarea>
              <p class="form-info-text">
                Go straight to the point and explain what the company does in a few sentences.
              </p>

              <p class="form-title-text">
                What will you do in your first month?
              </p>
              <textarea
                v-model="month3"
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
              ></textarea>

              <p class="form-title-text">What will you do in 3 months?</p>
              <textarea
                v-model="month6"
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
              ></textarea>

              <p class="form-title-text">What will you do in 6 months?</p>
              <textarea
                v-model="introduction"
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
              ></textarea>

              <p class="form-title-text">How will you do it?</p>
              <textarea
                v-model="howWillYou"
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
              ></textarea>

              <p class="form-title-text">With who will you do it?</p>
              <textarea
                v-model="whoWill"
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
              ></textarea>

              <p class="form-title-text">Where you will do it?</p>
              <textarea
                v-model="where"
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
              ></textarea>

              <p class="form-title-text">How your schedule will look like?</p>
              <textarea
                v-model="whatSchedule"
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
              ></textarea>

              <p class="form-title-text">
                What skills will you need for this position?
              </p>
              <textarea
                v-model="whatSkills"
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
              ></textarea>

              <p class="form-title-text">What will be your responsibilities?</p>
              <textarea
                v-model="whatResponsibilities"
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
              ></textarea>

              <p class="form-title-text">What we offer?</p>
              <textarea
                v-model="whatWeOffer"
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
              ></textarea>

              <p class="form-title-text">How can you apply?</p>
              <textarea
                v-model="howIApply"
                class="input-container text-area-container-medium"
                cols="50"
                rows="5"
                placeholder="Write your text here"
                style="width: 100%;"
              ></textarea>

              <p v-if="errors.showJobDescriptionMissing" class="p-error">
                ⚠️ showJobDescriptionMissing
              </p>
            </div>

            <p v-if="errors.showJobDescriptionMissing" class="p-error">
              ⚠️ showJobDescriptionMissing
            </p>
          </div>
        </div>

        <!-- Apply method -->
        <div class="form-container">
          <p class="form-title-text">APPLY METHOD*</p>

          <input
            type="checkbox"
            @change="
              useEmail = !useEmail;
              useWebsite = !useWebsite;
            "
            :checked="useEmail"
            style="margin-bottom: 1.5rem; margin-top: 1rem;"
          />
          Candidates apply via email
          <input
            type="checkbox"
            @change="
              useEmail = !useEmail;
              useWebsite = !useWebsite;
            "
            :checked="useWebsite"
            style="margin-bottom: 1.5rem; margin-top: 0.5rem;"
          />
          Candidates apply via website &nbsp;
          <div style="margin-bottom: 1rem;">
            <span class="featured-text">
              Easy for a candidate to apply than having to send an email
            </span>
          </div>
          <input
            v-if="useEmail"
            class="input-container input-container-medium"
            style="width: 100%;"
            placeholder="Example: email@email.com"
            ref="applyEmail"
            v-model="applyEmail"
            @click="select($refs.applyEmail, applyEmail)"
            type="text"
            maxlength="1000"
          />
          <input
            v-if="useWebsite"
            class="input-container input-container-medium"
            style="width: 100%;"
            placeholder="Example: https://domain.com"
            ref="applyWebsite"
            v-model="applyWebsite"
            @click="select($refs.applyWebsite, applyWebsite)"
            type="text"
            maxlength="1000"
          />
          <p class="form-info-text">
            This is how candidates will apply to the job offer, sending an email or going to a specific URL to apply.
          </p>

          <p v-if="errors.showApplyMethodMissing" class="p-error">
            ⚠️ showApplyMethodMissing
          </p>
        </div>

        <!-- Contract type -->
        <div class="form-container">
          <p class="form-title-text">CONTRACT TYPE</p>

          <input v-model="hasContractType" type="checkbox" style="margin-bottom: 1.5rem; margin-top: 1rem;" />Show contract type &nbsp;
          <div style="margin-bottom: 1rem;">
            <span class="featured-text">Increase candidates quality that are searching a specific contract type</span>
          </div>

          <select
            class="input-container input-container-medium"
            style="margin-right:0rem;"
            v-if="hasContractType"
            ref="contractType"
            v-model="contractType"
          >
            <option value="fullTime">Full Time</option>
            <option value="partTime">Part Time</option>
            <option value="contract">Contract</option>
            <option value="other">Other</option>
          </select>

          <div v-if="hasContractType">
            <input
              placeholder="Describe your contract type"
              class="input-container input-container-medium"
              style="width: 100%; margin-top: 0.5rem;"
              v-if="contractType === 'other'"
              ref="contractTypeOther"
              type="text"
              maxlength="1000"
              v-model="contractTypeOther"
              @click="select($refs.contractTypeOther, contractTypeOther)"
            />
          </div>

          <p class="form-info-text">
            Optional. Recommended to find candidates that fit for a specific type of contract.
          </p>
        </div>

        <!-- Salary range -->
        <div class="form-container">
          <p class="form-title-text">SALARY RANGE</p>

          <input v-model="hasSalaryRange" type="checkbox" style="margin-bottom: 0.5rem; margin-top: 1rem;" />Show salary range &nbsp;
          <div style="margin-top: 1rem;">
            <span class="featured-text">The more transparent you're, the more applicants you'll get</span>
          </div>

          <div v-if="hasSalaryRange">
            <p class="form-title-text">From</p>
            <input
              class="input-container input-container-medium"
              style="width: 100%;"
              placeholder="Example: $25000"
              ref="salaryStart"
              v-model="salaryStart"
              @click="select($refs.salaryStart, salaryStart)"
              type="number"
              @keyup.enter="select($refs.salaryEnd, salaryEnd)"
              maxlength="1000"
            />
            <p class="form-title-text">To</p>
            <input
              class="input-container input-container-medium"
              style="width: 100%;"
              placeholder="Example: $30000"
              ref="salaryEnd"
              v-model="salaryEnd"
              @click="select($refs.salaryEnd, salaryEnd)"
              type="number"
              maxlength="1000"
            />
            <select class="input-container input-container-medium" style="margin-top: 0.5rem;" v-model="currency">
              <option value="$-USD">$-USD</option>
              <option value="€-EUR">€-EUR</option>
              <option value="¥-YEN">¥-YEN</option>
              <option value="£-GBP">£-GBP</option>
              <option value="CHF">CHF</option>
              <option value="CAD-$">CAD-$</option>
            </select>
          </div>

          <p class="form-info-text">
            Optional. Highly recommended to add, your job offer will have more visibility on Google.
          </p>
        </div>
      </div>

      <div class="center-items">
        <button @click="step++" class="primary-button" style="padding: 1rem 3rem;" type="button">
          Next step <i class="fas fa-arrow-right next-page-button-icon"></i>
        </button>
      </div>
    </div>
  </div>

  <div v-if="step === 2" id="step3">
    <!-- Show toast after they publish job offer and are redirected to Dashboard with text: Thank you! We will send your job 🚀 -->
    <div class="page-container">
      <div class="features-container">
        <!-- Company name -->
        <div class="form-container">
          <p class="form-title-text">COMPANY NAME*</p>
          <input
            class="input-container input-container-medium"
            style="width: 100%;"
            placeholder="Example: Apple Corporation"
            ref="companyName"
            v-model="companyName"
            @click="select($refs.companyName, companyName)"
            type="text"
            maxlength="1000"
          />
          <p class="form-info-text">Add the company/brand name.</p>

          <p v-if="errors.showCompanyNameMissing" class="p-error">
            ⚠️ showCompanyNameMissing
          </p>
        </div>

        <!-- Company logo -->
        <div class="form-container">
          <p class="form-title-text">COMPANY LOGO</p>

          <input v-model="hasCompanyLogo" type="checkbox" style="margin-bottom: 1.5rem; margin-top: 0.5rem;" />
          Show company logo &nbsp;
          <div style="margin-bottom: 1rem;">
            <span class="featured-text">A company logo increases x4 the candidates click ratio of a job offer</span>
          </div>
          <img class="big-logo" v-if="preview && hasCompanyLogo" ref="logoPreview" :src="preview" alt="Your logo" />
          <div v-if="hasCompanyLogo">
            <input v-if="hasCompanyLogo" ref="companyLogo" type="file" @change="filePreview" />
          </div>

          <p class="form-info-text">
            Optional. Adding a company logo increases credibility and professionalism towards the candidates.
          </p>
        </div>

        <!-- Company website -->
        <div class="form-container">
          <p class="form-title-text">COMPANY WEBSITE</p>

          <input v-model="hasCompanyWebsite" type="checkbox" style="margin-bottom: 1.5rem; margin-top: 0.5rem;" />
          Show company website &nbsp;
          <div style="margin-bottom: 1rem;">
            <span class="featured-text">Helps applicants to know more about you and thus get better matches</span>
          </div>

          <input
            v-if="hasCompanyWebsite"
            class="input-container input-container-medium"
            style="width: 100%;"
            placeholder="Example: https://companywebsite.com"
            ref="companyWebsite"
            v-model="companyWebsite"
            @click="select($refs.companyWebsite, companyWebsite)"
            type="text"
            maxlength="1000"
          />
          <p class="form-info-text">
            Optional. Highly recommended to help applicants know more about your company and needs.
          </p>
        </div>
      </div>

      <div v-if="selectedSites.length === 0" class="center-items">
        <button @click="step = 0" class="primary-button primary-button-disabled" style="padding: 1rem 3rem;" type="button">
          Select at least one site 🚀
        </button>
      </div>

      <div v-if="selectedSites.length > subscription.purchasedJobPosts" class="center-items">
        <button @click="$router.push({ path: '/pricing' })" class="primary-button" style="padding: 1rem 3rem;" type="button">
          You have {{ subscription.purchasedJobPosts }} tokens and selected {{ selectedSites.length }} sites. Purchase more tokens 🚀
        </button>
      </div>

      <div
        v-if="selectedSites.length <= subscription.purchasedJobPosts && selectedSites.length !== 0 && subscription.purchasedJobPosts !== 0"
        class="center-items"
      >
        <button @click="postJob()" class="primary-button" style="padding: 1rem 3rem;" type="button">
          Send job offer to {{ selectedSites.length }} sites 🚀
        </button>
      </div>

      <div v-if="selectedSites.length !== 0 && subscription.purchasedJobPosts === 0" class="center-items">
        <button @click="$router.push({ path: '/pricing' })" class="primary-button" style="padding: 1rem 3rem;" type="button">
          Purchase tokens to send job offer to {{ selectedSites.length }} sites 🚀
        </button>
      </div>

      <p v-if="errors.showPostJobError" class="p-error">⚠️ showPostJobError</p>
      <p v-if="errors.showApplyMethodMissing" class="p-error">
        ⚠️ showApplyMethodMissing
      </p>
      <p v-if="errors.showJobTitleMissing" class="p-error">
        ⚠️ showJobTitleMissing
      </p>
      <p v-if="errors.showCompanyNameMissing" class="p-error">
        ⚠️ showCompanyNameMissing
      </p>
      <p v-if="errors.showLocationMissing" class="p-error">
        ⚠️ showLocationMissing
      </p>
      <p v-if="errors.showSelectSitesMissing" class="p-error">
        ⚠️ showSelectSitesMissing
      </p>
      <p v-if="errors.showJobDescriptionMissing" class="p-error">
        ⚠️ showJobDescriptionMissing
      </p>
    </div>
  </div>
</template>

<script>
// https://www.npmjs.com/package/simple-markdown
import firebase from "@/firebase";
import * as validator from "email-validator";
import utils from "@/utils";

import Toast from "@/components/Toast";
import SitesGrid from "@/components/SitesGrid";

export default {
  props: {
    maxSites: {
      type: Number,
      default: 3,
    },
    textareaMaxLength: {
      type: Number,
      default: 5000,
    },
    inputMaxLength: {
      type: Number,
      default: 500,
    },
  },

  created() {
    window.scrollTo(0, 0);
  },

  components: {
    Toast,
    SitesGrid,
  },

  data() {
    return {
      utils,
      selectedSites: [],
      subscription: {
        purchasedJobPosts: 0,
        jobsPosted: 0,
        remainingPosts: 0,
        totalPostsThisPeriod: 0,
        activeUserPlans: [],
        userPlans: [],
      },
      step: 0,
      steps: [
        {
          title: "Let's start",
          subtitle: "STEP 1 - Select sites to send your job offer",
        },
        {
          title: "Fill the form",
          subtitle: "STEP 2 - Add job offer info",
        },
        {
          title: "Add company info",
          subtitle: "STEP 3 - Add info about the company",
        },
      ],
      useEmail: utils.localStorageParse("useEmail", "Boolean", true),
      useWebsite: utils.localStorageParse("useWebsite", "Boolean"),
      showLocations: utils.localStorageParse("showLocations", "Boolean"),
      useComplexDescription: utils.localStorageParse("useComplexDescription", "Boolean"),
      hasSalaryRange: utils.localStorageParse("hasSalaryRange", "Boolean"),
      hasContractType: utils.localStorageParse("hasContractType", "Boolean"),
      isFullRemote: utils.localStorageParse("isFullRemote", "Boolean", true),
      hasCompanyLogo: utils.localStorageParse("hasCompanyLogo", "Boolean"),
      hasCompanyWebsite: utils.localStorageParse("hasCompanyWebsite", "Boolean"),

      contractType: utils.localStorageParse("contractType", "String", "fulltime"),
      applyWebsite: utils.localStorageParse("applyWebsite", "String"),
      applyEmail: utils.localStorageParse("applyEmail", "String"),
      companyWebsite: utils.localStorageParse("companyWebsite", "String"),
      companyName: utils.localStorageParse("companyName", "String"),
      jobTitle: utils.localStorageParse("jobTitle", "String"),
      location: utils.localStorageParse("location", "String"),
      jobDescription: utils.localStorageParse("jobDescription", "String"),
      preview: utils.localStorageParse("preview", "String"),
      introduction: utils.localStorageParse("introduction", "String"),
      month1: utils.localStorageParse("month1", "String"),
      month3: utils.localStorageParse("month3", "String"),
      month6: utils.localStorageParse("month6", "String"),
      howWillYou: utils.localStorageParse("howWillYou", "String"),
      whoWill: utils.localStorageParse("whoWill", "String"),
      whatSchedule: utils.localStorageParse("whatSchedule", "String"),
      where: utils.localStorageParse("where", "String"),
      whatSkills: utils.localStorageParse("whatSkills", "String"),
      whatResponsibilities: utils.localStorageParse("whatResponsibilities", "String"),
      whatWeOffer: utils.localStorageParse("whatWeOffer", "String"),
      howIApply: utils.localStorageParse("howIApply", "String"),
      contractTypeOther: utils.localStorageParse("contractTypeOther", "String"),
      salaryStart: utils.localStorageParse("salaryStart", "Number"),
      salaryEnd: utils.localStorageParse("salaryEnd", "Number"),
      currency: utils.localStorageParse("currency", "String", "$-USD"),
      countriesNames: utils.localStorageParse("countriesNames", "ArrayString"),

      imageBlob: utils.localStorageParse("imageBlob", "String"),
      errors: {
        showPostJobError: false,
        showApplyMethodMissing: false,
        showJobTitleMissing: false,
        showCompanyNameMissing: false,
        showLocationMissing: false,
        showSelectSitesMissing: false,
        showJobDescriptionMissing: false,
      },
    };
  },

  mounted() {
    firebase.functions
      .httpsCallable("getSubscriptionState")()
      .then((r) => {
        this.subscription = r.data;
      })
      .catch((e) => console.error(e));

    this.$refs.sitesGrid.getSites();
    this.countriesNames = utils.countryNames;
    this.$forceUpdate();
  },

  watch: {
    selectedSites(n) {
      localStorage.selectedSites = JSON.stringify(n);
    },
    step() {
      window.scrollTo(0, 0);
    },
    useEmail(n) {
      localStorage.useEmail = n;
    },
    useWebsite(n) {
      localStorage.useWebsite = n;
    },
    applyWebsite(n) {
      localStorage.applyWebsite = n;
    },
    applyEmail(n) {
      localStorage.applyEmail = n;
    },
    showLocations(n) {
      localStorage.showLocations = n;
    },
    useComplexDescription(n) {
      localStorage.useComplexDescription = n;
    },
    hasSalaryRange(n) {
      localStorage.hasSalaryRange = n;
    },
    hasContractType(n) {
      localStorage.hasContractType = n;
    },
    isFullRemote(n) {
      localStorage.isFullRemote = n;
    },
    hasCompanyLogo(n) {
      localStorage.hasCompanyLogo = n;
    },
    hasCompanyWebsite(n) {
      localStorage.hasCompanyWebsite = n;
    },
    salaryStart(n) {
      localStorage.salaryStart = n;
    },
    salaryEnd(n) {
      localStorage.salaryEnd = n;
    },
    currency(n) {
      localStorage.currency = n;
    },
    companyWebsite(n) {
      localStorage.companyWebsite = n;
    },
    contractType(n) {
      localStorage.contractType = n;
    },
    companyName(n) {
      localStorage.companyName = n;
    },
    jobTitle(n) {
      localStorage.jobTitle = n;
    },
    location(n) {
      localStorage.location = n;
    },
    jobDescription(n) {
      localStorage.jobDescription = n;
    },
    preview(n) {
      localStorage.preview = n;
    },
    introduction(n) {
      localStorage.introduction = n;
    },
    month1(n) {
      localStorage.month1 = n;
    },
    month3(n) {
      localStorage.month3 = n;
    },
    month6(n) {
      localStorage.month6 = n;
    },
    howWillYou(n) {
      localStorage.howWillYou = n;
    },
    whoWill(n) {
      localStorage.whoWill = n;
    },
    whatSchedule(n) {
      localStorage.whatSchedule = n;
    },
    where(n) {
      localStorage.where = n;
    },
    whatSkills(n) {
      localStorage.whatSkills = n;
    },
    whatResponsibilities(n) {
      localStorage.whatResponsibilities = n;
    },
    whatWeOffer(n) {
      localStorage.whatWeOffer = n;
    },
    howIApply(n) {
      localStorage.howIApply = n;
    },
    sites(n) {
      localStorage.sites = JSON.stringify(n);
    },

    countriesNames(n) {
      localStorage.countriesNames = n;
    },
    imageBlob(n) {
      localStorage.imageBlob = n;
    },
  },

  methods: {
    setSelectedSitesLocalStorage() {
      localStorage.selectedSites = JSON.stringify(this.selectedSites);
    },
    showLocationInput() {
      return this.location === "" && !this.isFullRemote;
    },

    hasToDisableAddSiteButton() {
      return this.selectedSites.length === this.maxSites;
    },

    clearSiteFilter() {
      this.sitesFilter.region = "";
      this.sitesFilter.country = "";
      this.sitesFilter.niche = "";
      this.sitesFilter.text = "";
    },

    cleanLocalStorage() {
      localStorage.removeItem("useEmail");
      localStorage.removeItem("useWebsite");
      localStorage.removeItem("applyWebsite");
      localStorage.removeItem("applyEmail");
      localStorage.removeItem("showLocations");
      localStorage.removeItem("useComplexDescription");
      localStorage.removeItem("hasSalaryRange");
      localStorage.removeItem("hasContractType");
      localStorage.removeItem("isFullRemote");
      localStorage.removeItem("hasCompanyLogo");
      localStorage.removeItem("hasCompanyWebsite");
      localStorage.removeItem("salaryStart");
      localStorage.removeItem("salaryEnd");
      localStorage.removeItem("companyWebsite");
      localStorage.removeItem("contractType");
      localStorage.removeItem("companyName");
      localStorage.removeItem("jobTitle");
      localStorage.removeItem("location");
      localStorage.removeItem("jobDescription");
      localStorage.removeItem("preview");
      localStorage.removeItem("introduction");
      localStorage.removeItem("month1");
      localStorage.removeItem("month3");
      localStorage.removeItem("month6");
      localStorage.removeItem("howWillYou");
      localStorage.removeItem("whoWill");
      localStorage.removeItem("whatSchedule");
      localStorage.removeItem("where");
      localStorage.removeItem("whatSkills");
      localStorage.removeItem("whatResponsibilities");
      localStorage.removeItem("whatWeOffer");
      localStorage.removeItem("howIApply");
      localStorage.removeItem("selectedSites");
    },

    filePreview() {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.preview = e.target.result;
        this.imageBlob = e.target.result;
      };

      const file = this.$refs.companyLogo.files[0];

      if (!file) {
        this.preview = "";
        this.imageBlob = "";
        return;
      }

      reader.readAsDataURL(file);
    },

    validEmail(email) {
      return validator.validate(email);
    },

    validate() {
      this.errors.showPostJobMissing = false;
      this.errors.showPostJobError = false;
      this.errors.showApplyMethodMissing = false;
      this.errors.showJobTitleMissing = false;
      this.errors.showCompanyNameMissing = false;
      this.errors.showLocationMissing = false;
      this.errors.showSelectSitesMissing = false;
      this.errors.showJobDescriptionMissing = false;

      let hasError = false;

      if (this.useEmail) {
        if (!this.validEmail(this.applyEmail)) {
          this.errors.showApplyMethodMissing = true;
          hasError = true;
        }
      } else {
        if (!utils.validURL(this.applyWebsite)) {
          this.errors.showApplyMethodMissing = true;
          hasError = true;
        }
      }

      if (this.jobTitle === "") {
        this.errors.showJobTitleMissing = true;
        hasError = true;
      }

      if (this.companyName === "") {
        this.errors.showCompanyNameMissing = true;
        hasError = true;
      }

      if (!this.isFullRemote && this.location === "") {
        this.errors.showLocationMissing = true;
        hasError = true;
      }

      if (this.$refs.sitesGrid.selectedSites.length === 0) {
        this.errors.showSelectSitesMissing = true;
        hasError = true;
      }

      if (this.useComplexDescription) {
        const isValidComplex =
          this.introduction !== "" &&
          this.month1 !== "" &&
          this.month3 !== "" &&
          this.month6 !== "" &&
          this.howWillYou !== "" &&
          this.whoWill !== "" &&
          this.whatSchedule !== "" &&
          this.where !== "" &&
          this.whatSkills !== "" &&
          this.whatResponsibilities !== "" &&
          this.whatWeOffer !== "" &&
          this.howIApply !== "";

        if (!isValidComplex) {
          this.errors.showJobDescriptionMissing = true;
          hasError = true;
        }
      } else {
        if (this.jobDescription === "") {
          this.errors.showJobDescriptionMissing = true;
          hasError = true;
        }
      }

      if (hasError) {
        this.errors.showPostJobMissing = true;
      }

      return !hasError;
    },

    getJobDescription() {
      if (this.useComplexDescription) {
        const n = "\n\n";
        return (
          "introduction" +
          n +
          this.introduction +
          n +
          "month1" +
          n +
          this.month1 +
          n +
          "month3" +
          n +
          this.month3 +
          n +
          "month6" +
          n +
          this.month6 +
          n +
          "howWillYou" +
          n +
          this.howWillYou +
          n +
          "whoWill" +
          n +
          this.whoWill +
          n +
          "whatSchedule" +
          n +
          this.whatSchedule +
          n +
          "where" +
          n +
          this.where +
          n +
          "whatSkills" +
          n +
          this.whatSkills +
          n +
          "whatResponsibilities" +
          n +
          this.whatResponsibilities +
          n +
          "whatWeOffer" +
          n +
          this.whatWeOffer +
          n +
          "howIApply" +
          n +
          this.howIApply
        );
      }

      return this.jobDescription;
    },

    select(ref, model) {
      ref.focus();
      ref.setSelectionRange(0, model.length);
    },

    async postJob() {
      if (!this.validate()) {
        return;
      }

      const Paddle = window.Paddle;
      Paddle.Spinner.show();

      const jobDescription = this.getJobDescription();
      const job = {
        selectedSites: this.$refs.sitesGrid.selectedSites,
        userId: firebase.auth.currentUser.uid,
        companyName: this.companyName,
        jobTitle: this.jobTitle,
        jobDescription,
        useEmail: this.useEmail,
        applyEmail: this.applyEmail,
        applyWebsite: this.applyWebsite,
        salaryStart: this.salaryStart,
        salaryEnd: this.salaryEnd,
        currency: this.currency,
        companyWebsite: this.companyWebsite,
        contractType: this.contractType,
        isFullRemote: this.isFullRemote,
        location: this.location,
        hasCompanyLogo: this.hasCompanyLogo,
      };
      try {
        const postJobResponse = await firebase.functions.httpsCallable("postJob")({ job });

        if (postJobResponse.data.errors.length !== 0) {
          alert(postJobResponse.data.errors.join("\n"));
          Paddle.Spinner.hide();
          return;
        }

        if (job.hasCompanyLogo) {
          if (!(await utils.uploadImage(this.imageBlob, "company-post-logos", postJobResponse.data.id, "png"))) {
            this.errors.showPostJobError = true;
            Paddle.Spinner.hide();
            return;
          }
        }

        this.cleanLocalStorage();
        this.$refs.toast.text = "Posted!";
        await this.$refs.toast.show();
        this.$router.push({ path: "/dashboard" });
      } catch (e) {
        console.error(e);
        this.errors.showPostJobError = true;
      }

      Paddle.Spinner.hide();
    },
  },
};
</script>

<style>
.big-logo {
  max-width: 600px;
  max-height: 600px;
}
.big-logo {
  max-width: 600px;
  max-height: 600px;
}
.tooltip {
  position: relative;
  display: inline-block;
}
.tooltiptext {
  margin-top: 70px;
  position: absolute;
  z-index: 1;
  max-width: 300px;
}
</style>
